/**
 * The public site's illustrations, shown as images.
 *
 * ## Where they come from
 *
 * `scripts/site-art.py` draws each one as an SVG in `public/art/`, from the same palette and parts
 * as the blog's pictures. They are files rather than components because they are drawings, not
 * interface: a change to one is an edit to the script and a re-run, and the page that shows it does
 * not change at all. `FeatureArt`, `AutomationArt` and `MonitorArt` are the names the pages already
 * import, and each is now one line that says which picture it is.
 *
 * ## The same in both themes, on purpose
 *
 * The first set were outlines in `currentColor`, which made them correct in light and dark and
 * pale in both: a thin line on a near-black card does not read as a diagram, it reads as a
 * placeholder. These are drawn on a dark panel with colour that means something (blue for what the
 * platform does, green for passed, amber for waiting, red for failed), and shown as a picture
 * inside a border, the way a screenshot of the product would be.
 *
 * ## Size and loading
 *
 * Every picture is 1100 by 500, so the box exists before the file arrives and nothing moves when
 * it does. A picture at the top of an article is the thing the reader came for and loads at once;
 * every other one waits until it is near the screen.
 */

export type ArtName =
  | 'playbooks'
  | 'hardening'
  | 'command-runner'
  | 'templates'
  | 'safe-rollouts'
  | 'run-history'
  | 'server-management'
  | 'team'
  | 'auto-fix'
  | 'backups'
  | 'restore-test'
  | 'monitoring'
  | 'website-monitoring'
  | 'api-monitoring'
  | 'ssl-monitoring'
  | 'dns-monitoring'
  | 'domain-expiry'
  | 'port-monitoring'
  | 'response-checks'
  | 'incident-record'
  | 'cert-countdown'
  | 'dns-lookup'
  | 'registration'
  | 'port-states';

/** What each picture shows, for somebody who cannot see it. */
const DESCRIPTION: Record<ArtName, string> = {
  playbooks:
    'One playbook, Update Packages, running on an Ubuntu, a Debian and a Rocky Linux server, each passing its checks.',
  hardening:
    'Connections from the internet meeting a firewall: SSH is let through to the server, while MySQL and telnet are blocked.',
  'command-runner':
    'One command sent to four servers: three return their output and one is unreachable, with a retry for the failed one.',
  templates: 'A WordPress Server template running four steps in order and leaving a server ready for WordPress.',
  'safe-rollouts':
    'A rollout in five stages: preflight, dry run, one canary server, your confirmation, then batches that stop on failure.',
  'run-history': 'A list of past runs with their outcomes, beside the output one server returned.',
  'server-management':
    'Techie Gain connected over SSH, with no agent, to Ubuntu, Debian, Rocky Linux and Amazon Linux servers.',
  team: 'A team made up of an owner, a member and a pending invitation, and the three steps of inviting someone.',
  'auto-fix':
    'A check failing, a safe fix restarting the service, and the check recovering without an alert being sent.',
  backups:
    'A server sending its files and database through a signed link to its owner’s storage bucket, which keeps dated copies.',
  'restore-test':
    'A backup fetched back, its checksum verified, loaded into a scratch database and every table read back.',
  monitoring:
    'Checks from outside the network on a website, API, port, SSL certificate, DNS record and domain, leading to an incident and alerts.',
  'website-monitoring':
    'A monitor requesting a web page from outside and checking the status code, the response time and a phrase on the page.',
  'api-monitoring':
    'A request to a health endpoint whose response is checked for status 200, a fast answer and the word ok.',
  'ssl-monitoring':
    'A certificate with 21 days left and a warning set at 30 days, with the ports it can be checked on.',
  'dns-monitoring':
    'The DNS records of a domain, two matching their expected values and one TXT record that has changed.',
  'domain-expiry': 'A domain’s expiry date read from the registry, with 42 days left to renew.',
  'port-monitoring':
    'A server’s ports, with SSH, HTTP, HTTPS and PostgreSQL accepting connections and SMTP refusing.',
  'response-checks': 'A check requesting a site and testing the status code, the response time and the expected text.',
  'incident-record':
    'A row of check results with a short outage in the middle, recorded as an incident lasting five minutes.',
  'cert-countdown': 'A timeline from today to a certificate’s expiry, with the warning raised before it expires.',
  'dns-lookup': 'A name resolved and the answer compared with the expected value, with the mismatch reported.',
  registration:
    'A domain registration running out while the server keeps running normally and nothing on it can see the date.',
  'port-states': 'A TCP connection accepted with SYN, SYN-ACK and ACK, and another refused with a reset.',
};

/** Every picture there is, for the check that measures their text (`scripts/blog-art/fit-check.js`). */
export const ART_NAMES = Object.keys(DESCRIPTION) as ArtName[];

export interface ArtProps {
  /** The picture is above the fold and is what the reader came for: load it at once. */
  priority?: boolean | undefined;
}

export function Art({ name, priority = false }: { name: ArtName } & ArtProps) {
  return (
    <img
      src={`/art/${name}.svg`}
      alt={DESCRIPTION[name]}
      width={1100}
      height={500}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      className="border-border/60 block h-auto w-full rounded-xl border"
    />
  );
}
